// Import React Libraries
import React, { useContext, useMemo, useRef } from "react";
import { AppContext } from "../../providers/Redux/Reducers/AppContext";
import { useHistory } from "react-router-dom";

// Import Ionic Components
import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCol,
  IonGrid,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonLoading,
  IonRow,
  useIonViewWillEnter,
} from "@ionic/react";

// Import Icons
import { briefcase } from "ionicons/icons";

// Import Styles
import "./my-orders.scss";
import styles from "./my-orders.module.scss";
import classNames from "classnames/bind";

// Import Components
import MaxWidthContainer from "../../components/UI/MaxWidthContainer/MaxWidthContainer";
import { useAuth } from "../../contexts/auth-context";
import { OrderPaymentStatus, TOrder } from "@core/types";
import { useInfiniteGetOrdersQuery } from "../../providers/Orders";
import { useConfig } from "../../providers/config";
import Page from "../page-layout";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);

const MyOrders: React.FC = () => {
  let history = useHistory();
  let infinite = useRef(null as any);
  const page = useRef<number>(1);
  const { state } = useContext(AppContext);
  const { user } = useAuth();
  const ionContentRef = useRef<HTMLIonContentElement>(null);
  const config = useConfig();
  const { t } = useTranslation();

  const {
    data: orders,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isRefetching,
  } = useInfiniteGetOrdersQuery({
    params: { page: page.current, customerId: user?.id ?? "", per_page: 10 },
    options: {
      enabled: !!user?.id,
    },
  });

  useIonViewWillEnter(() => {
    if (history.action === "PUSH") {
      ionContentRef.current?.scrollToTop();
      page.current = 1;
    }
  }, [state, history, orders, isLoading]);

  const showOrderDetail = (order: TOrder) => {
    history.push("/my-order-detail/" + order.id);
  };

  const openShop = () => {
    history.push("search-main-categories");
  };

  return (
    <Page>
      <Page.HeaderMobile showBackButton showSearchButton />
      <Page.Content>
        <Page.HeaderDesktop />
        <div className={`global-content ${cx({ myOrders__emptyContent: !orders?.pages.flat().length })}`}>
          <MaxWidthContainer
            maxWidthBreakpoint={"Md"}
            classList={cx({ myOrders__emptyContent: !orders?.pages.flat().length })}
          >
            <IonLoading isOpen={isRefetching || isLoading} message={t("Please wait...")} duration={8000} />

            {!orders?.pages.flat().length && (
              <IonGrid class="ion-no-padding grid-my-orders">
                <IonRow>
                  <IonCol className="ion-text-center" size="12">
                    <IonIcon icon={briefcase} />
                  </IonCol>
                  <IonCol className="ion-text-center" size="12">
                    <h4>{t("Your Order List is Empty")}</h4>
                  </IonCol>
                  <IonCol className="ion-text-center" size="12">
                    <h5>{t("Continue Shopping")}</h5>
                  </IonCol>
                  <IonCol className="ion-text-center" size="12">
                    <IonButton onClick={() => openShop()}>{t("Explore")}</IonButton>
                  </IonCol>
                </IonRow>
              </IonGrid>
            )}

            {!!orders?.pages.length && !isRefetching && (
              <IonGrid>
                {orders?.pages.flat().map((order: TOrder) => {
                  return (
                    <IonCard key={order.id} onClick={() => showOrderDetail(order)}>
                      <IonCardContent className={cx("myOrders__cardContent")}>
                        {order.status === "completed" && !order.driverRating && (
                          <IonBadge color="primary">{t("Rate the delivery")}</IonBadge>
                        )}
                        <IonRow>
                          <IonCol size="6">{t("Orders ID")}:</IonCol>
                          <IonCol class="ion-text-right" size="6">
                            {"#" + order.number}
                          </IonCol>
                        </IonRow>
                        <IonRow>
                          <IonCol size="6">{t("Date")}:</IonCol>
                          <IonCol class="ion-text-right" size="6">
                            {order.dateCreated?.split("T")[0].split("-").reverse().join(".")}
                          </IonCol>
                        </IonRow>
                        <IonRow>
                          <IonCol size="6">
                            <strong>{t("Total cost")}:</strong>
                          </IonCol>
                          <IonCol class="ion-text-right" size="6">
                            <strong>{order.total + " ₽"}</strong>
                          </IonCol>
                        </IonRow>

                        <IonRow className="items-start">
                          <IonCol size="4">{t("Status")}:</IonCol>
                          <IonCol className={cx("flex justify-end gap-1 flex-wrap")} size="8">
                            {(order.needsPayment || order?.paymentStatus === OrderPaymentStatus.PAYED) && (
                              <strong
                                className={cx("orderStatusBar", {
                                  inProcess: order.paymentStatus === OrderPaymentStatus.WAITING_PAYMENT,
                                  complete: order.paymentStatus === OrderPaymentStatus.PAYED,
                                  cancel: order.paymentStatus === OrderPaymentStatus.FAILED,
                                })}
                              >
                                {/* @ts-ignore */}
                                {t(order.paymentStatus)}
                              </strong>
                            )}
                            <strong
                              className={cx("orderStatusBar", {
                                cancel: order.status === "cancelled" || order.status === "failed",
                                inProcess:
                                  order.status === "processing" ||
                                  order.status === "pending" ||
                                  order.status === "out-for-delivery" ||
                                  order.status === "pending-delivery",
                                complete: order.status === "completed",
                                new: order.status === "new",
                              })}
                            >
                              {t(order.status!) || order.status}
                            </strong>
                          </IonCol>
                        </IonRow>
                      </IonCardContent>
                    </IonCard>
                  );
                })}
              </IonGrid>
            )}
            {config?.orderHistoryPagination && (
              <IonInfiniteScroll
                ref={infinite}
                threshold="100px"
                onIonInfinite={async () => {
                  if (hasNextPage) {
                    await fetchNextPage();
                  }
                  infinite.current?.complete();
                }}
              >
                <IonInfiniteScrollContent
                  loadingText={t("Please Wait")}
                  loadingSpinner="bubbles"
                ></IonInfiniteScrollContent>
              </IonInfiniteScroll>
            )}
          </MaxWidthContainer>
        </div>
        <Page.ContentFooter />
      </Page.Content>
    </Page>
  );
};
export default MyOrders;
